import { useCallback, useEffect, useRef, useState } from "react";

export interface UseFloatingDropdownReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dropdownStyle: React.CSSProperties;
  updateDropdownPosition: () => void;
  handleFocus: () => void;
  handleBlur: () => void;
}

interface UseFloatingDropdownOptions {
  /** Triggered when the input gains focus, before opening. */
  onFocus?: () => void;
  /** Delay before closing on blur, ms. Lets click events register. Default 150. */
  blurDelayMs?: number;
  /** Minimum dropdown width in px. Default 280. */
  minWidthPx?: number;
}

/**
 * Manages a fixed-position floating dropdown anchored to a container ref.
 * Used by inline select components that render a dropdown above table cells
 * without clipping by overflow.
 */
export function useFloatingDropdown(
  options: UseFloatingDropdownOptions = {}
): UseFloatingDropdownReturn {
  const { onFocus, blurDelayMs = 150, minWidthPx = 280 } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, minWidthPx),
      zIndex: 99999,
    });
  }, [minWidthPx]);

  useEffect(() => {
    if (isOpen) updateDropdownPosition();
  }, [isOpen, updateDropdownPosition]);

  const handleFocus = useCallback(() => {
    onFocus?.();
    setIsOpen(true);
    updateDropdownPosition();
  }, [onFocus, updateDropdownPosition]);

  const handleBlur = useCallback(() => {
    setTimeout(() => setIsOpen(false), blurDelayMs);
  }, [blurDelayMs]);

  return {
    containerRef,
    isOpen,
    setIsOpen,
    dropdownStyle,
    updateDropdownPosition,
    handleFocus,
    handleBlur,
  };
}
